import type { TabsProps } from '@mui/material/Tabs';
import type { Theme, SxProps } from '@mui/material/styles';

import { useIsClient } from 'minimal-shared/hooks';

import Tabs from '@mui/material/Tabs';
import { styled } from '@mui/material/styles';
import { tabClasses } from '@mui/material/Tab';

// ----------------------------------------------------------------------

type CustomTabsSlotProps = TabsProps['slotProps'] & {
  tab?: { sx?: SxProps<Theme> };
  list?: { sx?: SxProps<Theme> };
  indicator?: { sx?: SxProps<Theme> };
  indicatorContent?: React.ComponentProps<typeof IndicatorContent>;
};

export type CustomTabsProps = TabsProps & {
  slotProps?: CustomTabsSlotProps;
};

const customTabsStyles: Record<string, SxProps<Theme>> = {
  root: {
    flexShrink: 0,
    bgcolor: 'background.neutral',
  },
  list: {
    p: 1,
    height: 1,
    gap: { xs: 0 },
  },
  indicator: {
    py: 1,
    height: 1,
    bgcolor: 'transparent',
  },
  tabItem: {
    px: 2,
    zIndex: 1,
    minHeight: 'auto',
  },
};

export function CustomTabs({ children, slotProps, sx, ...other }: CustomTabsProps) {
  const isClient = useIsClient();

  return (
    <Tabs
      sx={[
        customTabsStyles.root,
        {
          [`& .${tabClasses.root}`]: {
            ...customTabsStyles.tabItem,
            ...slotProps?.tab?.sx,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      slotProps={{
        ...slotProps,
        indicator: {
          ...slotProps?.indicator,
          children: isClient && <IndicatorContent sx={slotProps?.indicatorContent?.sx} />,
          sx: [
            customTabsStyles.indicator,
            ...(Array.isArray(slotProps?.indicator?.sx)
              ? slotProps.indicator.sx
              : [slotProps?.indicator?.sx]),
          ],
        },
        list: {
          ...slotProps?.list,
          sx: [
            customTabsStyles.list,
            ...(Array.isArray(slotProps?.list?.sx) ? slotProps.list.sx : [slotProps?.list?.sx]),
          ],
        },
      }}
      {...other}
    >
      {children}
    </Tabs>
  );
}

// ----------------------------------------------------------------------

const IndicatorContent = styled('span')(({ theme }) => ({
  zIndex: 1,
  width: '100%',
  height: '100%',
  display: 'block',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.vars.customShadows.z1,
  backgroundColor: theme.vars.palette.common.white,
  ...theme.applyStyles('dark', {
    backgroundColor: theme.vars.palette.grey[900],
  }),
}));
